import { useCallback, useLayoutEffect, useState } from 'react';

import { DROPDOWN_PLACEMENT_GAP } from './consts';
import type { DropdownPlacementState, UseDropdownPlacementOptions } from './types';

const useDropdownPlacement = ({
  containerRef,
  getTopBoundary,
  isOpen,
  repositionKey,
  gap = DROPDOWN_PLACEMENT_GAP,
  maxHeight: maxHeightOption,
  minHeight,
  preferredHeight,
  viewportGap,
}: UseDropdownPlacementOptions): DropdownPlacementState => {
  const [state, setState] = useState<DropdownPlacementState>({
    isOpenAbove: false,
    maxHeight: maxHeightOption,
  });

  const updatePlacement = useCallback(() => {
    const containerElement = containerRef.current;

    if (!containerElement) {
      return;
    }

    const containerRect = containerElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const boundaryBottom = viewportHeight - viewportGap;
    const boundaryTop = Math.max(viewportGap, getTopBoundary?.(containerElement) ?? viewportGap);

    const availableBelow = Math.max(0, boundaryBottom - containerRect.bottom - gap);
    const availableAbove = Math.max(0, containerRect.top - boundaryTop - gap);
    const desiredHeight = Math.max(minHeight, preferredHeight);
    const isOpenAbove = availableBelow < desiredHeight && availableAbove > availableBelow;
    const available = isOpenAbove ? availableAbove : availableBelow;
    // The dropdown must fit into the actually usable viewport. A CSS min-height
    // would otherwise push it over persistent controls on short screens.
    const maxHeight = Math.min(maxHeightOption, Math.max(0, available));

    const fixedPosition: NonNullable<DropdownPlacementState['fixedPosition']> = {
      top: isOpenAbove ? containerRect.top - gap - maxHeight : containerRect.bottom + gap,
      left: containerRect.left,
      width: containerRect.width,
    };

    setState((currentState) => {
      const currentPosition = currentState.fixedPosition;
      const hasSamePosition =
        currentPosition?.top === fixedPosition.top &&
        currentPosition?.left === fixedPosition.left &&
        currentPosition?.width === fixedPosition.width;

      return currentState.isOpenAbove === isOpenAbove && currentState.maxHeight === maxHeight && hasSamePosition
        ? currentState
        : { isOpenAbove, maxHeight, fixedPosition };
    });
  }, [containerRef, gap, getTopBoundary, viewportGap, preferredHeight, maxHeightOption, minHeight]);

  useLayoutEffect(() => {
    const containerElement = containerRef.current;

    if (!isOpen || !containerElement) {
      return undefined;
    }

    updatePlacement();

    const animationFrame =
      typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame(updatePlacement) : undefined;
    const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updatePlacement);
    let observedElement: HTMLElement | null = containerElement;

    while (observedElement) {
      resizeObserver?.observe(observedElement);
      observedElement = observedElement.parentElement;
    }

    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);

    return () => {
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }

      resizeObserver?.disconnect();
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [
    isOpen,
    repositionKey,
    state.fixedPosition?.left,
    state.fixedPosition?.top,
    state.fixedPosition?.width,
    state.maxHeight,
    updatePlacement,
  ]);

  return state;
};

export default useDropdownPlacement;
